import { Currency, CurrencyAmount } from '@uniswap/sdk-core'
import { useMemo } from 'react'
import { useCheckApprovalQuery } from 'uniswap/src/data/apiClients/tradingApi/useCheckApprovalQuery'
import { ApprovalRequest, ApprovalResponse, Routing } from 'uniswap/src/data/tradingApi/__generated__/index'
import { AccountMeta } from 'uniswap/src/features/accounts/types'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import {
  convertGasFeeToDisplayValue,
  useActiveGasStrategy,
  useShadowGasStrategies,
} from 'uniswap/src/features/gas/hooks'
import { areEqualGasStrategies } from 'uniswap/src/features/gas/types'
import { ApprovalAction, TokenApprovalInfo } from 'uniswap/src/features/transactions/swap/types/trade'
import { isUniswapX } from 'uniswap/src/features/transactions/swap/utils/routing'
import {
  getTokenAddressForApi,
  toTradingApiSupportedChainId,
} from 'uniswap/src/features/transactions/swap/utils/tradingApi'
import { GasFeeEstimates } from 'uniswap/src/features/transactions/types/transactionDetails'
import { WrapType } from 'uniswap/src/features/transactions/types/wrap'
import { logger } from 'utilities/src/logger/logger'
import { ONE_MINUTE_MS, ONE_SECOND_MS } from 'utilities/src/time/time'

export interface TokenApprovalInfoParams {
  chainId: UniverseChainId
  wrapType: WrapType
  currencyInAmount: Maybe<CurrencyAmount<Currency>>
  currencyOutAmount?: Maybe<CurrencyAmount<Currency>>
  routing: Routing | undefined
  account?: AccountMeta
  skip?: boolean
}

interface TokenApprovalGasInfo {
  gasFee?: string
  displayGasFee?: string
  cancelGasFee?: string
  displayCancelGasFee?: string
  gasEstimates?: GasFeeEstimates
  isLoading: boolean
}

export function useTokenApprovalInfo(params: TokenApprovalInfoParams): TokenApprovalInfo & TokenApprovalGasInfo {
  const { account, chainId, wrapType, currencyInAmount, currencyOutAmount, routing, skip } = params

  const isWrap = wrapType !== WrapType.NotApplicable

  const address = account?.address
  const inputWillBeWrapped = routing && isUniswapX({ routing })
  // 🔧 关键修复：直接使用原始 currency，不要转换成 wrapped
  // getTokenAddressForApi 会正确处理：原生代币 → 0x000...，ERC20 → 实际地址
  const currencyIn = currencyInAmount?.currency
  const amount = currencyInAmount?.quotient.toString()

  const tokenInAddress = getTokenAddressForApi(currencyIn)

  // Only used for bridging
  const isBridge = routing === Routing.BRIDGE
  const currencyOut = currencyOutAmount?.currency
  const tokenOutAddress = getTokenAddressForApi(currencyOut)

  const activeGasStrategy = useActiveGasStrategy(chainId, 'general')
  const shadowGasStrategies = useShadowGasStrategies(chainId, 'general')

  const approvalRequestArgs: ApprovalRequest | undefined = useMemo(() => {
    const tokenInChainId = toTradingApiSupportedChainId(chainId)
    const tokenOutChainId = toTradingApiSupportedChainId(currencyOut?.chainId)

    if (!address || !amount || !currencyIn || !tokenInAddress || !tokenInChainId) {
      return undefined
    }
    if (isBridge && !tokenOutAddress && !tokenOutChainId) {
      return undefined
    }

    const args = {
      walletAddress: address,
      token: tokenInAddress,
      amount,
      chainId: tokenInChainId,
      includeGasInfo: true,
      tokenOut: tokenOutAddress,
      tokenOutChainId,
      gasStrategies: [activeGasStrategy, ...(shadowGasStrategies ?? [])],
    }
    return args
  }, [
    activeGasStrategy,
    address,
    amount,
    chainId,
    currencyIn,
    currencyOut?.chainId,
    isBridge,
    tokenInAddress,
    tokenOutAddress,
    shadowGasStrategies,
  ])

  const shouldSkip = skip || !approvalRequestArgs || isWrap || !address

  const { data, isLoading, error, refetch } = useCheckApprovalQuery({
    params: shouldSkip ? undefined : approvalRequestArgs,
    // 🔧 减少 staleTime 到 1 秒，让授权完成后能更快重新查询
    staleTime: 1 * ONE_SECOND_MS,
    immediateGcTime: ONE_MINUTE_MS,
    // 🔧 启用重新聚焦时自动刷新，授权完成后切回页面会自动更新
    refetchOnWindowFocus: true,
    // 🔧 智能轮询策略：
    // - 无需授权时：不轮询
    // - 需要授权时：初次较快轮询（2秒），用于检测授权完成
    refetchInterval: (query) => {
      const approvalData = query.state.data as ApprovalResponse | undefined
      
      // 兼容新旧 API 响应格式
      const hasApproval = !!(approvalData?.approval || (approvalData as any)?.dataApproval)
      
      if (!hasApproval) {
        // 无需授权，停止轮询
        return false
      }
      
      // 需要授权时，每 2 秒轮询一次检查是否已完成
      return 2 * ONE_SECOND_MS
    },
  })

  return useMemo(() => {
    if (error) {
      logger.error(error, {
        tags: { file: 'useTokenApprovalInfo', function: 'useTokenApprovalInfo' },
        extra: {
          approvalRequestArgs,
        },
      })
    }

    if (isWrap) {
      return {
        action: ApprovalAction.None,
        txRequest: null,
        cancelTxRequest: null,
        isLoading,
      }
    }

    if (data && !error) {
      // 🔧 兼容两种 API 响应格式：data.approval 或 data.dataApproval
      const approval = (data as any).approval ?? (data as any).dataApproval
      const cancel = (data as any).cancel ?? (data as any).dataCancel
      const gasFee = (data as any).gasFee
      const cancelGasFee = (data as any).cancelGasFee
      const gasEstimatesData = (data as any).gasEstimates
      

      
      // API returns null if no approval is required
      if (approval === null) {
        return {
          action: ApprovalAction.None,
          txRequest: null,
          cancelTxRequest: null,
          isLoading,
        }
      }

      if (approval) {
        // 🔍 DEBUG: 检查 gasEstimates 结构
        
        const activeEstimate = gasEstimatesData?.find((e: any) => areEqualGasStrategies(e.strategy, activeGasStrategy))

        let gasEstimates: GasFeeEstimates | undefined
        if (activeEstimate) {
          gasEstimates = {
            activeEstimate,
            shadowEstimates: gasEstimatesData?.filter((e: any) => e !== activeEstimate),
          }
        }

        // 🔧 关键修复：默认不执行 revocation，除非 API 明确要求
        // Permit2 设计允许直接覆盖旧授权，无需先重置为 0
        // 只在特殊情况下（如某些代币合约要求）才需要 revocation
        const explicitlyRequiresRevocation = (data as any).requiresRevocation === true
        
        if (explicitlyRequiresRevocation && cancel) {
          console.log('⚠️ API 要求先撤销授权（罕见情况）', { token: tokenInAddress })
          
          // 🔧 同样从 gasEstimates 提取 gas fee
          const revocationGasFee = activeEstimate?.gasFee ?? (typeof gasFee === 'object' ? (gasFee as any).gasFee : gasFee)
          const revocationCancelGasFee = typeof cancelGasFee === 'object' ? (cancelGasFee as any).gasFee : cancelGasFee
          
          return {
            action: ApprovalAction.RevokeAndPermit2Approve,
            txRequest: approval,
            gasFee: revocationGasFee,
            displayGasFee: convertGasFeeToDisplayValue(revocationGasFee, activeGasStrategy),
            cancelTxRequest: cancel,
            cancelGasFee: revocationCancelGasFee,
            displayCancelGasFee: convertGasFeeToDisplayValue(revocationCancelGasFee, activeGasStrategy),
            isLoading,
            gasEstimates,
          }
        }

        // 🔧 FIX: For cases where cancel is provided but not required, treat as normal approval
        // and include cancel as optional cancelTxRequest
        
        // 🔧 关键修复：提取 gasFee 值
        // 优先级：
        // 1. gasEstimates 中的 activeEstimate.gasFee（最准确）
        // 2. 顶层 gasFee 字段
        // 3. gasFee.gasFee 嵌套字段
        let extractedGasFee: string | undefined
        
        // 优先从 gasEstimates 获取
        if (activeEstimate?.gasFee) {
          extractedGasFee = activeEstimate.gasFee
        } else if (typeof gasFee === 'string') {
          extractedGasFee = gasFee
        } else if (typeof gasFee === 'object' && gasFee !== null) {
          extractedGasFee = (gasFee as any).gasFee ?? (gasFee as any).gasLimit
        }
        
        let extractedCancelGasFee: string | undefined
        if (cancel) {
          // 同样优先从 gasEstimates 获取（如果有 cancel 对应的 estimate）
          if (typeof cancelGasFee === 'string') {
            extractedCancelGasFee = cancelGasFee
          } else if (typeof cancelGasFee === 'object' && cancelGasFee !== null) {
            extractedCancelGasFee = (cancelGasFee as any).gasFee ?? (cancelGasFee as any).gasLimit
          }
        }
        
        return {
          action: ApprovalAction.Permit2Approve,
          txRequest: approval,
          gasFee: extractedGasFee,
          displayGasFee: convertGasFeeToDisplayValue(extractedGasFee, activeGasStrategy),
          gasEstimates,
          cancelTxRequest: cancel || null, // Include cancel as optional
          cancelGasFee: extractedCancelGasFee,
          displayCancelGasFee: cancel ? convertGasFeeToDisplayValue(extractedCancelGasFee, activeGasStrategy) : undefined,
          isLoading,
        }
      }
    }

    // No valid approval type found
    return {
      action: ApprovalAction.Unknown,
      txRequest: null,
      cancelTxRequest: null,
      isLoading,
    }
  }, [activeGasStrategy, approvalRequestArgs, data, error, isWrap, isLoading])
}
